import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import {
  Alert, Button, Collapse, Form, Input, InputNumber, Modal, Select, Switch,
  Tabs, Tooltip, Typography, Upload,
} from "antd";
import {
  ArrowLeftOutlined, ArrowRightOutlined, EyeOutlined, InfoCircleOutlined,
  PictureOutlined, PlusOutlined,
} from "@ant-design/icons";
import "./product-editor.css";

const { Text } = Typography;
const GENERAL_FIELDS = ["warehouse", "product_type", "stock_management_mode", "nom_produit",
  "id_categorie", "item_code", "slug", "barcode_symbology", "barcode_value"];

// Presentation only: writes, conversions, permissions and stock calculations remain
// in GestionProduit. Both tab panels stay mounted so submission retains every field.
const ProductEditorModal = forwardRef(function ProductEditorModal({
  form, open, editingProduct, activeTab, onTabChange, onClose, onSubmit, onFinishFailed,
  variantMode, canConfigureVariants, onToggleVariants, subscriptionWriteRestricted,
  submitting, options, actions, formatting, referencesNotice, referenceConflicts = [],
  assistantNotice, catalogSuggestions, stockExtras, variantContent, galleryContent,
  assistantFooter,
}, ref) {
  const [referencesOpen, setReferencesOpen] = useState(false);
  const [advancing, setAdvancing] = useState(false);
  const navigationLock = useRef(false);
  const productType = Form.useWatch("product_type", form);
  const productName = Form.useWatch("nom_produit", form);
  const warehouseId = Form.useWatch("warehouse", form);
  const unitId = Form.useWatch("id_unit", form);
  const barcode = Form.useWatch("barcode_value", form);
  const stockMode = Form.useWatch("stock_management_mode", form);
  const isRaw = productType === "Matières premières";
  const title = editingProduct ? `Modifier ${isRaw ? "la matière première" : "le produit"}`
    : isRaw ? "Nouvelle matière première" : "Nouveau produit";
  const warehouse = options.warehouses.find((item) => String(item.id) === String(warehouseId));
  const unit = options.units.find((item) => String(item.id) === String(unitId));
  const unitShort = unit?.short_name;
  const inputPrecision = isRaw ? 4 : 0;
  const revealField = (field) => {
    if (["item_code", "slug"].includes(field)) setReferencesOpen(true);
  };
  useImperativeHandle(ref, () => ({ revealField }));

  const fail = (error) => {
    revealField(error?.errorFields?.[0]?.name?.[0]);
    onFinishFailed(error);
  };
  const next = async (event) => {
    // The next render can replace this button by the final submit button before
    // the browser performs the click's default action. Cancel that native action.
    event?.preventDefault();
    if (navigationLock.current) return;
    navigationLock.current = true;
    setAdvancing(true);
    try {
      if (activeTab === "1") {
        await form.validateFields(GENERAL_FIELDS.filter((name) => name !== "barcode_value" || !variantMode));
      } else if (activeTab === "2") {
        await form.validateFields(variantMode ? ["id_unit", "taxe"]
          : ["id_unit", "quantite_stock", "alerte_quantite", "prix_achat", "prix_vente", "taxe"]);
      }
      onTabChange(activeTab === "1" ? "2" : "variants");
    } catch (error) {
      if (!error.outOfDate) fail(error);
    } finally {
      navigationLock.current = false;
      setAdvancing(false);
    }
  };
  const hasNext = activeTab === "1" || (activeTab === "2" && variantMode);
  const previous = activeTab === "variants" ? "2" : activeTab === "3" ? "2" : "1";
  const categoryMissing = !options.loading && options.verified && options.categories.length === 0;
  const numberFormat = (value) => formatting.quantity(value, isRaw);
  const priceFormat = (value) => formatting.price(value, isRaw).replace(/\s*CFA$/, "");
  const referenceErrors = (name) => referenceConflicts.filter((item) => item.field === name).map((item) => item.message);
  const conflictProps = (name) => referenceErrors(name).length ? { validateStatus: "error", help: referenceErrors(name).join(" ") } : {};

  const general = <div className="product-editor-panel">
    <div className={`product-editor-context-grid${canConfigureVariants ? " has-variants" : ""}`}>
      <Form.Item name="warehouse" label="Établissement" rules={[{ required: true, message: "Veuillez sélectionner un établissement" }]}
        {...conflictProps("warehouse")}>
        <Select placeholder="Sélectionner un établissement" disabled={Boolean(editingProduct && variantMode)}
          options={options.warehouses.map((item) => ({ value: item.id, label: item.name }))}
          onChange={actions.changeWarehouse} />
      </Form.Item>
      <Form.Item name="product_type" label="Type de produit" rules={[{ required: true, message: "Veuillez sélectionner un type" }]}>
        <Select disabled={variantMode} options={[
          { value: "Produits finis", label: "Produits finis" },
          { value: "Matières premières", label: options.rawMaterialLabel || "Matières premières" },
        ]} />
      </Form.Item>
      {canConfigureVariants && <div className="product-editor-variants">
        <label htmlFor="product-editor-variant-switch">
          {editingProduct ? "Convertir en variantes" : "Produit à variantes"}
          <Tooltip title={editingProduct
            ? "Le produit actuel deviendra une déclinaison et conservera son stock, ses codes et son historique."
            : "Chaque déclinaison possède son stock, ses prix et son code-barres. L’activation sélectionne le type Produits finis."}>
            <InfoCircleOutlined tabIndex={0} aria-label="Aide sur les variantes" />
          </Tooltip>
        </label>
        <div className="product-editor-variant-control">
          <Switch id="product-editor-variant-switch" checked={variantMode} onChange={onToggleVariants}
            disabled={submitting || subscriptionWriteRestricted} />
          <Text type="secondary">{variantMode ? "Activé" : "Désactivé"}</Text>
        </div>
      </div>}
      <Form.Item name="stock_management_mode" label="Gestion du stock"
        rules={[{ required: true, message: "Veuillez sélectionner un mode de stock" }]}
        extra="Standard : suivi des quantités.">
        <Select options={[{ value: "standard", label: "Standard" }, { value: "pre_order", label: "Précommande" }, { value: "unlimited", label: "Illimité" }]} />
      </Form.Item>
    </div>
    <section className="product-editor-section">
      <h3>Identité {isRaw ? "de la matière première" : "du produit"}</h3>
      <div className="product-editor-identity">
        <div className="product-editor-image">
          <Form.Item name="image" valuePropName="fileList" getValueFromEvent={(event) => Array.isArray(event) ? event : event?.fileList}>
            <Upload listType="picture-card" maxCount={1} beforeUpload={() => false}>
              <span className="product-editor-image-trigger"><PictureOutlined aria-hidden="true" /><span>Ajouter une image</span></span>
            </Upload>
          </Form.Item>
          <Text type="secondary">Facultatif</Text>
        </div>
        <div className="product-editor-identity-fields">
          <Form.Item name="nom_produit" label={`Nom ${isRaw ? "de la matière première" : "du produit"}`}
            hasFeedback validateTrigger={["onChange", "onBlur"]} rules={[
              { required: true, message: "Veuillez entrer le nom du produit" },
              { validateTrigger: "onBlur", validator: async (_, value) => {
                const name = String(value || "").trim();
                if (!name || !warehouseId) return;
                const error = await actions.checkProductExists("name", name, warehouseId, editingProduct?.id || null);
                if (error) throw new Error(error);
              } },
            ]}>
            <Input placeholder={`Nom ${isRaw ? "de la matière première" : "du produit"}`} onChange={(event) => actions.changeName(event.target.value)} />
          </Form.Item>
          {catalogSuggestions}
          <div className="product-editor-quick-field">
            <Form.Item name="id_categorie" label="Catégorie" rules={[{ required: true, message: "Veuillez sélectionner une catégorie" }]}
              validateStatus={categoryMissing ? "error" : undefined}
              help={categoryMissing ? (actions.canCreateCategory ? "Aucune catégorie disponible. Créez-en une pour continuer." : "Aucune catégorie disponible. Demandez la création d’une catégorie à un administrateur.") : undefined}
              {...conflictProps("id_categorie")}>
              <Select placeholder="Sélectionner une catégorie" showSearch optionFilterProp="label" allowClear loading={options.loading}
                disabled={!options.companyId || categoryMissing}
                options={options.categories.map((item) => ({ value: item.id, label: item.name }))} />
            </Form.Item>
            {actions.canCreateCategory && <Button icon={<PlusOutlined aria-hidden="true" />} onClick={actions.createCategory}
              disabled={!options.companyId || submitting || subscriptionWriteRestricted}>Créer une catégorie</Button>}
          </div>
          <Form.Item name="description" label={<span>Description <Text type="secondary" className="product-editor-optional">Facultatif</Text></span>}>
            <Input.TextArea placeholder={`Description ${isRaw ? "de la matière première" : "du produit"}`} rows={2} />
          </Form.Item>
        </div>
      </div>
    </section>
    <section className="product-editor-section">
      <h3>Code-barres</h3>
      <div className="product-editor-grid two-columns">
        <Form.Item name="barcode_symbology" label="Format du code-barres" rules={[{ required: true, message: "Veuillez sélectionner un type de code-barres" }]}>
          <Select placeholder="Sélectionner le format" options={[{ value: "CODE128", label: "CODE 128" }, { value: "EAN13", label: "EAN-13" }, { value: "CODE39", label: "CODE 39" }]} />
        </Form.Item>
        {!variantMode && <Form.Item name="barcode_value" label="Code-barres" htmlFor="product-editor-barcode-value" rules={[{ required: true, message: "Veuillez entrer ou générer un code-barres" }]}>
          <div className="product-editor-barcode">
            <Input id="product-editor-barcode-value" aria-label="Code-barres" placeholder="Saisir ou générer"
              value={barcode || ""} onChange={(event) => actions.changeBarcode(event.target.value)} />
            <Button icon={barcode ? <EyeOutlined aria-hidden="true" /> : undefined} onClick={barcode ? actions.showBarcode : actions.generateBarcode}
              disabled={submitting || (!barcode && subscriptionWriteRestricted)}>{barcode ? "Afficher" : "Générer"}</Button>
          </div>
        </Form.Item>}
      </div>
      {variantMode && <Text type="secondary">Les codes-barres sont définis dans l’onglet Déclinaisons.</Text>}
    </section>
    <Collapse className="product-editor-references" activeKey={referencesOpen ? ["references"] : []}
      onChange={(keys) => setReferencesOpen(keys.includes("references"))} items={[{
        key: "references", forceRender: true,
        label: <span>Références automatiques <Text type="secondary">Code article et adresse web générés depuis le nom</Text></span>,
        children: <div className="product-editor-grid two-columns">
          <Form.Item name="item_code" label="Code de l’article" required={false} rules={[{ required: true, message: "Le code article est requis" }]}>
            <Input readOnly aria-readonly="true" placeholder="Généré automatiquement" />
          </Form.Item>
          <Form.Item name="slug" label="Adresse du produit en ligne" required={false} rules={[{ required: true, message: "Le slug est requis" }]}>
            <Input readOnly aria-readonly="true" placeholder="Générée automatiquement" />
          </Form.Item>
        </div>,
      }]} />
  </div>;

  const stock = <div className="product-editor-panel product-editor-stock-panel">
    <div className="product-editor-recap">
      <div><strong>{productName || (isRaw ? "Matière première à créer" : "Produit à créer")}</strong>
        <Text type="secondary">{[warehouse?.name, productType, variantMode ? "Avec variantes" :
          ({ standard: "Stock standard", pre_order: "Précommande", unlimited: "Stock illimité" })[stockMode]].filter(Boolean).join(" · ")}</Text></div>
      <Button type="link" onClick={() => onTabChange("1")}>Modifier les informations</Button>
    </div>
    {variantMode && <Alert type="info" showIcon message="Mode variantes activé"
      description="Le stock, les prix et les codes sont définis dans l’onglet Déclinaisons. L’unité et la taxe s’appliquent à toutes les déclinaisons." />}
    <section className="product-editor-section product-editor-stock">
      <h3>Gestion du stock</h3>
      <p>{isRaw ? "Choisissez l’unité de référence pour le stock et la production." : "Définissez l’unité, le stock initial et le seuil d’alerte."}</p>
      <div className={`product-editor-grid stock-columns${variantMode ? " variants-stock" : ""}`}>
        <div className="product-editor-quick-field">
          <Form.Item name="id_unit" label={isRaw ? "Unité de stock" : "Unité de mesure"}
            extra={isRaw ? "Unité utilisée pour les consommations." : "Unité utilisée pour les quantités."}
            rules={[{ required: true, message: "Veuillez sélectionner une unité" }]} {...conflictProps("id_unit")}>
            <Select placeholder="Sélectionner une unité" showSearch optionFilterProp="label" allowClear loading={options.loading}
              disabled={!options.companyId}
              options={options.units.map((item) => ({ value: item.id, label: `${item.name} (${item.short_name})` }))} />
          </Form.Item>
          {actions.canCreateUnit && <Button icon={<PlusOutlined aria-hidden="true" />} onClick={actions.createUnit}
            disabled={!options.companyId || submitting || subscriptionWriteRestricted}>Créer une unité</Button>}
        </div>
        {!variantMode && <>
          <Form.Item name="quantite_stock" label={editingProduct ? "Stock actuel (lecture seule)" : "Stock initial"}
            tooltip={editingProduct ? "Utilisez les ajustements de stock pour modifier le stock actuel." : "Stock de départ lors de la création."}
            extra={editingProduct ? "Modification par ajustement de stock." : "Quantité disponible à la création."}
            rules={[{ required: true, type: "number", min: 0 }]}>
            <InputNumber min={0} step={isRaw ? 0.01 : 1} precision={inputPrecision} disabled={Boolean(editingProduct)}
              formatter={numberFormat} parser={(value) => formatting.parseQuantity(value, isRaw)} addonAfter={unitShort} />
          </Form.Item>
          <Form.Item name="alerte_quantite" label="Seuil d’alerte" tooltip="Seuil d’alerte pour le réapprovisionnement"
            extra="Seuil de réapprovisionnement." rules={[{ required: true, type: "number", min: 0 }]}>
            <InputNumber min={0} step={isRaw ? 0.01 : 1} precision={inputPrecision} formatter={numberFormat}
              parser={(value) => formatting.parseQuantity(value, isRaw)} addonAfter={unitShort} />
          </Form.Item>
        </>}
      </div>
    </section>
    <section className="product-editor-section">
      <h3>Prix et taxe</h3>
      <p>Renseignez les prix {isRaw ? "de la matière première" : "du produit"} et la taxe applicable.</p>
      <div className="product-editor-grid price-columns">
        {!variantMode && <>
          <Form.Item name="prix_achat" label="Prix d’achat" extra={isRaw && unitShort ? `Pour 1 ${unitShort}.` : null}
            rules={[{ required: true, type: "number", min: 0 }]}>
            <InputNumber min={0} step={isRaw ? 0.01 : 100} precision={inputPrecision} formatter={priceFormat}
              parser={(value) => formatting.parsePrice(value, isRaw)} addonAfter="CFA" />
          </Form.Item>
          <Form.Item name="prix_vente" label={isRaw ? "Prix d’entrée en production" : "Prix de vente"}
            extra={isRaw && unitShort ? `Pour 1 ${unitShort}.` : null} rules={[{ required: true, type: "number", min: 0 }]}>
            <InputNumber min={0} step={isRaw ? 0.01 : 100} precision={inputPrecision} formatter={priceFormat}
              parser={(value) => formatting.parsePrice(value, isRaw)} addonAfter="CFA" />
          </Form.Item>
        </>}
        <Form.Item name="taxe" label="Taxe applicable" extra="Facultatif" {...conflictProps("taxe")}
          normalize={(value) => value === "__none__" ? null : value}
          getValueProps={(value) => ({ value: value === null ? "__none__" : value })}>
          <Select placeholder="Sélectionner une taxe" allowClear options={[{ value: "__none__", label: "Aucune taxe" },
            ...options.taxes.map((item) => ({ value: item.id, label: `${item.name} (${item.rate}%)` }))]} />
        </Form.Item>
      </div>
    </section>
    {stockExtras}
    {!isRaw && !variantMode && <div className="product-editor-preserved">
      <InfoCircleOutlined aria-hidden="true" /> Les informations du premier onglet sont conservées.
    </div>}
  </div>;

  const items = [{ key: "1", label: "Informations générales", children: general, forceRender: true },
    { key: "2", label: "Stock & prix", children: stock, forceRender: true }];
  if (variantMode) items.push({ key: "variants", label: "Déclinaisons", children: variantContent, forceRender: true });
  if (editingProduct?.id && galleryContent) items.push({ key: "3", label: "Galerie E-commerce", children: galleryContent });

  return <Modal open={open} title={<div className="product-editor-heading"><span>{title}</span>
    <p>{isRaw ? "Identifiez la matière première, puis définissez son stock et ses prix."
      : "Ajoutez un produit à votre catalogue pour le vendre et suivre vos stocks."}</p></div>}
    onCancel={submitting ? undefined : onClose} footer={null} width={1264} destroyOnClose centered
    maskClosable={!submitting} keyboard={!submitting} closable={!submitting} className="product-editor-modal product-editor-workspace">
    <Form form={form} layout="vertical" onFinish={(values) => { if (hasNext) void next(); else onSubmit(values); }}
      onFinishFailed={fail} disabled={submitting} className="product-editor-form"
      initialValues={{ product_type: "Produits finis", stock_management_mode: "standard", alerte_quantite: 0,
        quantite_stock: 0, prix_achat: 0, prix_vente: 0, assistant_packaging_quantity: 0,
        assistant_packaging_purchase_price: 0, assistant_opening_package_count: 0, assistant_alert_package_count: 0 }}>
      {assistantNotice}
      {referencesNotice}
      <Tabs activeKey={activeTab} onChange={onTabChange} items={items} className="product-editor-tabs" />
      <div className="product-editor-footer">
        <Text type="secondary"><span className="product-editor-required">*</span> Champs obligatoires</Text>
        <div className="product-editor-actions">
          {assistantFooter}
          <Button onClick={onClose} disabled={submitting}>Annuler</Button>
          {activeTab !== "1" && <Button icon={<ArrowLeftOutlined aria-hidden="true" />} onClick={() => onTabChange(previous)} disabled={submitting}>Précédent</Button>}
          {hasNext ? <Button key="next" type="primary" htmlType="button" onClick={next} loading={advancing} disabled={submitting}>
            {activeTab === "1" ? "Suivant : stock et prix" : "Suivant : déclinaisons"} <ArrowRightOutlined aria-hidden="true" />
          </Button> : <Button key="submit" type="primary" htmlType="submit" loading={submitting}
            disabled={submitting || subscriptionWriteRestricted || referenceConflicts.length > 0} icon={editingProduct ? undefined : <PlusOutlined aria-hidden="true" />}>
            {variantMode ? editingProduct ? "Convertir en produit à variantes" : "Créer le produit à variantes"
              : editingProduct ? "Enregistrer les modifications" : `Créer ${isRaw ? "la matière première" : "le produit"}`}
          </Button>}
        </div>
      </div>
    </Form>
  </Modal>;
});

export default ProductEditorModal;
